// Plain-language text for the stable blocker codes and issuance states the AHA API returns.

export const CLASS_BLOCKER_TEXT = {
  AHA_NOT_SET_UP: "AHA eCards are not set up for this training center.",
  AHA_NOT_ON_CLASS: "This class does not issue AHA eCards.",
  AHA_LMS_ID_MISSING: "This training center's LMS ID has not been saved. An administrator adds it under Training Center › Certificates.",
  AHA_TC_ID_MISSING: "This training center's AHA Training Center ID has not been added. An administrator adds it under Training Center › Certificates.",
  AHA_DISABLED: "AHA eCards are not enabled in this environment.",
  AHA_PROFILE_INVALID: "This environment is not connected to an AHA environment.",
  AHA_CREDENTIALS_MISSING: "AHA credentials are not configured for this environment.",
  DISPATCH_DISABLED: "This server has AHA sending switched off (a server setting, AHA_DISPATCH_ENABLED), not your training center settings. Ask whoever runs the server to switch it on.",
  NON_DURABLE_DATABASE: "This environment cannot safely record card requests yet.",
  CLASS_NOT_CONFIGURED: "Choose the AHA product and modules for this class.",
  CLASS_CONFIG_INVALID: "The saved AHA product or modules are no longer valid. Configure the class again.",
  PRODUCT_NOT_VERIFIED: "The AHA product number has not been verified for this environment yet.",
  MODULE_NOT_AVAILABLE: "A configured module is not available in this environment.",
  NOT_AN_INSTRUCTOR: "Only an instructor assigned to this class can send AHA cards.",
  INSTRUCTOR_AHA_ID_MISSING:
    "The instructor teaching this class has no AHA instructor ID. Ask an administrator to add it under Instructor Management.",
};

export const STUDENT_BLOCKER_TEXT = {
  NOT_ENROLLED: "Not enrolled",
  NOT_SIGNED_OFF: "Instructor sign-off needed",
  MISSING_EMAIL: "Email is missing or invalid",
  MISSING_NAME: "First and last name needed",
  INVALID_NAME: "Name has unsupported characters",
  STUDENT_NOT_FOUND: "Student record not found",
  FUTURE_COMPLETION_DATE: "Completion date is in the future",
  NO_SESSION_MODULES: "No AHA modules from the sessions they attended",
  MODULES_INVALID: "Modules from their sessions don't make a valid card",
  CARD_ON_OTHER_REGISTRATION: "Card already requested on an earlier enrollment",
  SESSION_MISSED: "Missed a class session",
  SESSIONS_AFTER_COMPLETION: "Class has sessions after the sign-off date",
  TEST_NOT_PASSED: "A class test isn't passed yet",
  SKILLS_NOT_PASSED: "A required skill check isn't passed yet",
  ISSUANCE_PENDING: "Queued to send to AHA",
  ISSUANCE_SENDING: "Sending to AHA",
  ISSUANCE_UNKNOWN: "Result unknown — needs review",
  ISSUANCE_ASSIGNED: "Card issued",
};

// Why a request ended as it did (errorCode on the request). Shown next to its state so staff know what to do.
export const ERROR_CODE_TEXT = {
  INSUFFICIENT_INVENTORY: "Not enough AHA eCards in inventory — buy more, then try again",
  PROVIDER_REJECTED: "AHA rejected the request",
  DUPLICATE_REFERENCE: "AHA reported a duplicate request — check AHA's records",
  INVALID_STUDENT_DATA: "The student's name or email could not be sent",
  NOT_SENT: "The request was never sent to AHA",
  OUTCOME_UNKNOWN: "No clear answer from AHA — check AHA's records",
  UNEXPECTED_ERROR: "Something went wrong while sending — check AHA's records",
  MISSING_RESULT: "AHA's reply did not include this student — check AHA's records",
  INVALID_RESULT: "AHA's reply for this student could not be read — check AHA's records",
  INTERRUPTED: "Sending was interrupted — check AHA's records",
  MISSING_CLAIM_LINK: "Issued without a claim link",
  RESOLVED_NOT_ISSUED: "Checked with AHA: no card was issued",
  AUTHENTICATION_REJECTED: "AHA did not accept the platform's credentials",
  PROVIDER_UNAVAILABLE: "AHA was unavailable",
  INVALID_CONFIGURATION: "The AHA setup for this class is incomplete",
  MISSING_CREDENTIALS: "AHA credentials are not configured",
  DISPATCH_DISABLED: "AHA sending is switched off on this server",
  DISABLED: "AHA eCards are not enabled",
  NON_DURABLE_DATABASE: "This environment cannot safely record card requests",
};

export const errorCodeText = (code) => (code ? ERROR_CODE_TEXT[code] || null : null);

export const STATE_LABEL = {
  PENDING: "Queued",
  SENDING: "Sending",
  ASSIGNED: "Card issued",
  REJECTED: "Rejected by AHA",
  UNKNOWN: "Result unknown",
};

export const STATE_COLOR = {
  PENDING: "info",
  SENDING: "info",
  ASSIGNED: "success",
  REJECTED: "error",
  UNKNOWN: "warning",
};

// Blockers that mean AHA is simply not part of this class's world (the center has no AHA set up, or the class
// does not issue AHA eCards); the AHA UI stays hidden for these.
const HIDDEN_CLASS_BLOCKERS = ["AHA_NOT_SET_UP", "AHA_NOT_ON_CLASS", "AHA_DISABLED", "AHA_PROFILE_INVALID"];

export const blockerText = (code) => CLASS_BLOCKER_TEXT[code] || STUDENT_BLOCKER_TEXT[code] || code;

/** The AHA controls only appear for AHA-partner centers with the integration turned on. */
export function isAhaVisible(aha) {
  return !!aha && !(aha.classBlockers || []).some((b) => HIDDEN_CLASS_BLOCKERS.includes(b));
}

export function formatCompletionDate(isoDate) {
  if (!isoDate) return "—";
  const [year, month, day] = String(isoDate).split("-");
  return `${month}/${day}/${year}`;
}

/** DEV and QA are worth pointing out; production is the normal case and needs no label. */
export const isTestEnvironment = (environment) => !!environment && environment !== "PROD";

/**
 * What the class panel says about modules, or null when the AHA product has none (BLS, ACLS) or no product is set.
 * An older response without {@code productHasModules} is treated as a product with modules.
 */
export function classModuleText(aha) {
  const config = aha?.config;
  if (!config || config.productHasModules === false) return null;
  if (aha.moduleSource === "SESSIONS") return "Modules: from the sessions each student attended";
  if (aha.moduleNames?.length > 0) return `Required modules: ${aha.moduleNames.join(", ")}`;
  return "Modules: set them on each session in the Sessions tab";
}
